import { useTranslation } from "react-i18next";
import { BannerBase } from "../BannerBase";
import QR from "assets/icons/switcher-qr.png";
import { useEffect, useMemo, useState } from "react";
import { openExternalUrlInNewTab } from "helpers/navigation";
import { useHasPlanRole } from "hooks/useHasPlanRole";
import { useReferralLink } from "hooks/useReferralLink";
import referralImage from "assets/icons/referral.svg";
import { useSelector } from "react-redux";
import { RootState } from "store/reducers";

/*Referral banner should be shown to Studio users who have completed 3 or more livestreams
 and have not dismissed the banner. The banner will be dismissed for 7 days after the user clicks on it.*/
const REFERRAL_DISMISS_KEY = "referralBannerDismissed";
const REFERRAL_DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000;

export type DynamicActionBannerType = "downloadApp" | "referral";

/**
 * DynamicActionBanner
 *
 * This component displays context-aware banners to users based on their account state.
 *
 * Banner States:
 *  "downloadApp" - Shown to Studio users who haven't completed any livestreams
 *  Purpose: Encourage new Studio users to download the mobile app
 *
 *  "referral" - Shown to all users who have completed 3+ livestreams. Can be dismissed for 7 days via localStorage
 *  Purpose: Promote the referral program to engaged users
 *
 *  null - No banner shown when user doesn't match criteria for other states
 *
 */
export const DynamicActionBanner = () => {
    const { t } = useTranslation();
    const userInfo = useSelector((state: RootState) => state.user.userInfo);
    const livestreamCount =
        userInfo?.MetaData?.Details?.CompletedLivestreamCount;
    const isStudioUser = useHasPlanRole("Studio");
    const { copyLink } = useReferralLink();
    const [isReferralDismissed, setIsReferralDismissed] = useState(false);

    useEffect(() => {
        const dismissedTimestamp = localStorage.getItem(REFERRAL_DISMISS_KEY);
        if (dismissedTimestamp) {
            const timeElapsed = Date.now() - parseInt(dismissedTimestamp, 10);
            if (timeElapsed < REFERRAL_DISMISS_DURATION) {
                setIsReferralDismissed(true);
            }
        }
    }, []);

    const type = useMemo((): DynamicActionBannerType => {
        if (isStudioUser && livestreamCount < 1) {
            return "downloadApp";
        } else if (livestreamCount >= 3 && !isReferralDismissed) {
            return "referral";
        } else {
            return null;
        }
    }, [isReferralDismissed, isStudioUser, livestreamCount]);

    const bannerConfig = useMemo(() => {
        switch (type) {
            case "downloadApp":
                return {
                    header: t("dynamic-action:download:header"),
                    subheader: t("dynamic-action:download:subheader"),
                    buttonProps: {
                        label: t("dynamic-action:download:button-label"),
                        onClick: () =>
                            openExternalUrlInNewTab(
                                "https://itunes.apple.com/us/app/switcher-studio-pro/id908386221"
                            ),
                        type: "btn-primary" as const
                    },
                    imgSrc: QR,
                    imgClassName: undefined
                };
            case "referral":
                return {
                    header: t("dynamic-action:referral:header"),
                    subheader: t("dynamic-action:referral:subheader"),
                    buttonProps: {
                        label: t("dynamic-action:referral:button-label"),
                        onClick: () => {
                            copyLink();
                            localStorage.setItem(
                                REFERRAL_DISMISS_KEY,
                                Date.now().toString()
                            );
                        },
                        type: "btn-primary" as const
                    },
                    imgSrc: referralImage,
                    imgClassName: "referral-icon"
                };
            default:
                return null;
        }
    }, [type, t, copyLink]);

    return (
        <>
            {type != null ? (
                <BannerBase
                    header={bannerConfig.header}
                    subheader={bannerConfig.subheader}
                    buttonProps={{
                        label: bannerConfig.buttonProps.label,
                        onClick: bannerConfig.buttonProps.onClick,
                        type: bannerConfig.buttonProps.type
                    }}
                    imgSrc={bannerConfig.imgSrc}
                    imgClassName={bannerConfig.imgClassName}
                ></BannerBase>
            ) : null}
        </>
    );
};
